/**
 * `ContentPageFirstLoad` — the first-load shell shared by the two chrome-less
 * content `DETAIL_ROUTES` pages (`UserVideosPage`, `ContentDetailsPage`).
 *
 * Both pages early-return an identical structure while their first fetch is in
 * flight: a full-height column whose only body is a centered
 * `AnimatedLoadingIndicator`, with the title-less `ExpauseAppBar` rendered
 * ABOVE it. The bar is deliberately NOT gated on `isInitialized` / `isLoading`
 * — Flutter keeps the same bar over its empty/first-load body
 * (`lib/presentation/screens/content/custom_widgets/scrollable/content_list_widget.dart:1029-1048`,
 * loading body at `:1071-1078`), and a route with no back control strands a
 * deep-linked visitor (`workflow/lessons.md`). Owning that rule here, rather
 * than in each page, is what keeps it from silently regressing in one of them.
 *
 * Every `data-qa-*` handle is a REQUIRED prop with no default, so a caller
 * cannot render this shell without tagging both the page root and the bar root
 * — the two handles the branch's UI tests (`feat_reusable_app_bar/ui_test_9.md`,
 * `ui_test_10.md`) query the first-load window by.
 */

import { forwardRef } from 'react';

import { ExpauseAppBar } from '@/presentation/components/common/appBar/ExpauseAppBar';
import { AnimatedLoadingIndicator } from '@/presentation/components/common/loadingIndicators/LoadingIndicator';
import { qaAttr, type QaAttrOptions } from '@/presentation/utils/qaAttr';

export interface ContentPageFirstLoadProps {
    /**
     * `data-qa-*` for the PAGE root. Required: the page-root id + its `loading`
     * status token is what a UI test waits on to catch this window, and both
     * pages must keep spelling it exactly as their loaded branch does so the
     * `loading` → `ready` transition happens on ONE stable root.
     */
    qa: QaAttrOptions;
    /**
     * `data-qa-*` for the app bar's own root, forwarded to `ExpauseAppBar`'s
     * `rootQa`. Required so the bar box stays directly queryable here exactly as
     * it is in the loaded branch — a test that instead walks up from the leading
     * button to the nearest sticky ancestor resolves the wrong element. Pass a
     * feature-prefixed, page-unique id (`user-videos-app-bar`), never a bare
     * `app-bar`: `KeepAliveOutlet` keeps cached routes mounted, so several bars
     * sit in the document at once.
     */
    appBarQa: QaAttrOptions;
    /** `data-qa-*` forwarded to the bar's leading `BackIconButton`. */
    leadingQa: QaAttrOptions;
    /** Back handler for the bar's leading control (each page owns its own fallback). */
    onLeadingTap: () => void;
}

/**
 * Forwarded `ref` → the `ExpauseAppBar` root (NOT this shell's root), so the
 * page's `useMeasuredHeight` callback ref measures the same node in this branch
 * as in the loaded one. The callback ref (dis)connects itself as React mounts /
 * unmounts the bar, so the measured height survives the loading → loaded swap.
 */
export const ContentPageFirstLoad = forwardRef<HTMLDivElement, ContentPageFirstLoadProps>(({
    qa,
    appBarQa,
    leadingQa,
    onLeadingTap,
}, ref) => (
    <div {...qaAttr(qa)} className="min-h-screen flex flex-col">
        <ExpauseAppBar
            ref={ref}
            rootQa={appBarQa}
            leadingQa={leadingQa}
            onLeadingTap={onLeadingTap}
        />
        <div className="flex-1 flex items-center justify-center">
            <AnimatedLoadingIndicator />
        </div>
    </div>
));

ContentPageFirstLoad.displayName = 'ContentPageFirstLoad';
